import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, setAuthToken } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

type Step = 'confirming' | 'otp' | 'error';

export function ConfirmPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();

  const [step, setStep] = useState<Step>('confirming');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Auto-confirm invite token on mount
  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('No invite token found in the URL. Please use the link from your invitation email.');
      setStep('error');
      return;
    }

    authApi.confirmInvite(token)
      .then((res) => {
        setEmail(res.email);
        setStep('otp');
      })
      .catch((err) => {
        setError(err.message || 'Invalid or expired invite link. Ask your admin to resend the invitation.');
        setStep('error');
      });
  }, []);

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const res = await authApi.verifyOTP(email, otp.trim());
      setAuthToken(res.token);
      login(res.user);
      navigate('/home', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-primary flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-text-primary">CrossProd</h1>
          <p className="text-sm text-text-secondary mt-1">Product Roadmap Planning</p>
        </div>

        <div className="bg-surface-default rounded-xl border border-border-default p-8 shadow-sm">
          {/* Confirming step — spinner */}
          {step === 'confirming' && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              </div>
              <p className="text-sm text-text-secondary">Confirming your email...</p>
            </div>
          )}

          {/* OTP step */}
          {step === 'otp' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">Check your inbox</h2>
                <p className="text-sm text-text-secondary mt-1">
                  We sent a login code to <span className="font-medium text-text-primary">{email}</span>
                </p>
              </div>

              <form onSubmit={handleOtpSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    Login code
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                    className="w-full px-3 py-2.5 text-center text-xl font-mono tracking-widest border border-border-default rounded-lg bg-background-primary text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={otp.length !== 6 || isSubmitting}
                  className="w-full py-2.5 px-4 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
            </div>
          )}

          {/* Error step */}
          {step === 'error' && (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <h2 className="text-base font-semibold text-text-primary">Invite link invalid</h2>
                <p className="text-sm text-text-secondary mt-1">{error}</p>
              </div>
              <a
                href="/login"
                className="inline-block text-sm text-blue-600 hover:text-blue-700"
              >
                Back to login
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
